import { Button, ButtonProps, TextField, styled } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FC, useState } from 'react';
import { Modal } from '../../../ui';
import { add } from 'date-fns';

interface DateRangeModalProps {
  start: Date;
  end: Date;
  onSubmit: (start: Date, end: Date) => void;
  handleClose: () => void;
  open: boolean;
}

export const DateRangeModal: FC<DateRangeModalProps> = ({
  start,
  end,
  onSubmit,
  handleClose,
  open,
}) => {
  const handleSubmit = (start: Date, end: Date) => {
    onSubmit(start, end);
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <CustomDateRangePicker start={start} end={end} onSubmit={handleSubmit} />
    </Modal>
  );
};

interface CustomDateRangePickerProps {
  start: Date | null;
  end: Date | null;
  onSubmit: (startDate: Date, endDate: Date) => void;
  onSubmitButtonText?: string;
  buttonProps?: ButtonProps;
}

export const CustomDateRangePicker: FC<CustomDateRangePickerProps> = ({
  start,
  end,
  onSubmit,
  onSubmitButtonText = 'Apply',
  buttonProps = {
    variant: 'contained',
  },
}) => {
  const [startDate, setStartDate] = useState<Date | null>(start);
  const [endDate, setEndDate] = useState<Date | null>(end);

  const handleSubmit = () => {
    if (startDate && endDate) {
      onSubmit && onSubmit(startDate, endDate);
    }
  };

  return (
    <DatePickerContainer>
      <DateInputContainer>
        <DatePicker
          closeOnSelect
          label='Start Date'
          value={startDate}
          onChange={(newValue) => {
            setStartDate(newValue);
          }}
          renderInput={(params) => <TextField variant='standard' {...params} />}
        />
        <span>to</span>
        <DatePicker
          closeOnSelect
          label='End Date'
          value={endDate}
          onChange={(newValue) => {
            setEndDate(newValue);
          }}
          renderInput={(params) => <TextField variant='standard' {...params} />}
          disabled={startDate === null}
          minDate={startDate ? add(startDate, { days: 1 }) : undefined}
        />
      </DateInputContainer>
      <ButtonContainer>
        <Button {...buttonProps} onClick={handleSubmit}>
          {onSubmitButtonText}
        </Button>
      </ButtonContainer>
    </DatePickerContainer>
  );
};

const DatePickerContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px 0;
`;

const DateInputContainer = styled('div')`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 16px 0;
`;

const ButtonContainer = styled('div')`
  display: flex;
  justify-content: flex-end;
  padding: 16px 0;
`;
